import { findCodeRanges, inRanges, splitLinesWithOffsets } from './codeRanges.js';

export type SecretKind = 'dm' | 'comment';

export interface SecretRange {
  kind: SecretKind;
  /** Gesamter Bereich inkl. Begrenzer. */
  start: number;
  end: number;
  /** Inhalt ohne Begrenzer. */
  innerStart: number;
  innerEnd: number;
  /** Steht der Bereich allein auf eigenen Zeilen? */
  block: boolean;
}

const DM_OPEN = /^ {0,3}:{3,}[ \t]*dm[ \t]*$/i;
const DIRECTIVE_OPEN = /^ {0,3}:{3,}[ \t]*[A-Za-z]/;
const DIRECTIVE_CLOSE = /^ {0,3}:{3,}[ \t]*$/;

/**
 * Findet DM-only-Bereiche: `:::dm … :::`-Blöcke und Obsidian-Kommentare `%% … %%`.
 * Inhalte in Codeblöcken/Inline-Code zählen nicht. Nicht geschlossene Bereiche
 * reichen bis zum Ende (wie in Obsidian) – lieber zu viel verstecken als zu wenig.
 */
export function findSecretRanges(md: string): SecretRange[] {
  const code = findCodeRanges(md);
  const found: SecretRange[] = [];

  // 1. :::dm-Blöcke (zeilenbasiert)
  const lines = splitLinesWithOffsets(md);
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (inRanges(code, line.start) || !DM_OPEN.test(line.text)) continue;
    let depth = 1;
    let j = i + 1;
    for (; j < lines.length; j++) {
      const l = lines[j]!;
      if (inRanges(code, l.start)) continue;
      if (DIRECTIVE_CLOSE.test(l.text)) depth--;
      else if (DIRECTIVE_OPEN.test(l.text)) depth++;
      if (depth === 0) break;
    }
    const closed = j < lines.length;
    const endLine = closed ? lines[j]! : null;
    found.push({
      kind: 'dm',
      start: line.start,
      end: endLine ? endLine.end : md.length,
      innerStart: line.end,
      innerEnd: endLine ? endLine.start : md.length,
      block: true,
    });
    i = closed ? j : lines.length;
  }

  // 2. %%-Kommentare
  const dmRanges = found.map((r) => [r.start, r.end] as const);
  let open = -1;
  let p = 0;
  while (p < md.length) {
    const idx = md.indexOf('%%', p);
    if (idx === -1) break;
    if (inRanges(code, idx) || (open === -1 && inRanges(dmRanges, idx))) {
      p = idx + 2;
      continue;
    }
    if (open === -1) {
      open = idx;
    } else {
      found.push(commentRange(md, open, idx + 2));
      open = -1;
    }
    p = idx + 2;
  }
  if (open !== -1) found.push(commentRange(md, open, md.length, true));

  return mergeRanges(found);
}

function commentRange(md: string, start: number, end: number, unclosed = false): SecretRange {
  const lineStart = md.lastIndexOf('\n', start - 1) + 1;
  const before = md.slice(lineStart, start);
  const nl = md.indexOf('\n', end);
  const after = md.slice(end, nl === -1 ? md.length : nl);
  const block = before.trim() === '' && after.trim() === '';
  return { kind: 'comment', start, end, innerStart: start + 2, innerEnd: unclosed ? end : end - 2, block };
}

/** Verschachtelte/überlappende Bereiche zum äußeren zusammenfassen. */
function mergeRanges(ranges: SecretRange[]): SecretRange[] {
  const sorted = [...ranges].sort((a, b) => a.start - b.start || b.end - a.end);
  const out: SecretRange[] = [];
  for (const r of sorted) {
    const last = out[out.length - 1];
    if (last && r.start < last.end) {
      if (r.end > last.end) {
        last.end = r.end;
        last.innerEnd = Math.max(last.innerEnd, r.innerEnd);
      }
      continue;
    }
    out.push({ ...r });
  }
  return out;
}

/** Entfernt alle DM-only-Inhalte. Wird für jede Nicht-DM-Auslieferung verwendet. */
export function stripSecrets(md: string): string {
  const ranges = findSecretRanges(md);
  if (ranges.length === 0) return md;
  let out = '';
  let last = 0;
  for (const r of ranges) {
    out += md.slice(last, r.start);
    last = r.end;
    if (r.block) {
      // ganze Zeile(n) inkl. Einrückung und Umbruch entfernen
      out = out.replace(/[ \t]*$/, '');
      if (md[last - 1] !== '\n') {
        const nl = md.indexOf('\n', last);
        last = nl === -1 ? md.length : nl + 1;
      }
    }
  }
  out += md.slice(last);
  return out;
}

/** Enthält der Text DM-only-Inhalte? */
export function hasSecrets(md: string): boolean {
  return findSecretRanges(md).length > 0;
}

/**
 * Bereitet DM-only-Inhalte für die Darstellung beim DM auf: Blöcke werden zu
 * `<div class="dm-secret|dm-comment">`, Inline-Kommentare zu `<span class="dm-comment">`.
 * Das HTML wird anschließend von rehype-raw/rehype-sanitize verarbeitet.
 */
export function markSecretsForDm(md: string): string {
  const ranges = findSecretRanges(md);
  if (ranges.length === 0) return md;
  let out = '';
  let last = 0;
  for (const r of ranges) {
    out += md.slice(last, r.start);
    const inner = markSecretsForDm(md.slice(r.innerStart, r.innerEnd));
    const cls = r.kind === 'dm' ? 'dm-secret' : 'dm-comment';
    if (r.block) {
      // HTML-Block mit Leerzeilen, damit der Inhalt weiter als Markdown geparst wird
      out = out.replace(/[ \t]*$/, '');
      out += `<div class="${cls}">\n\n${inner.trim()}\n\n</div>\n\n`;
      last = r.end;
      if (md[last - 1] !== '\n') {
        const nl = md.indexOf('\n', last);
        last = nl === -1 ? md.length : nl + 1;
      }
    } else {
      out += `<span class="${cls}">${inner}</span>`;
      last = r.end;
    }
  }
  return out + md.slice(last);
}
